import './address-modal.scss';

import { Close } from '@mui/icons-material';
import {
	Box,
	CircularProgress,
	DialogActions,
	DialogContent,
	DialogTitle,
	DialogProps,
	FormControlLabel,
	Grid,
	IconButton,
	RadioGroup
} from '@mui/material';
import Modal from '../modal/modal';
import Typography from '../typography/typography';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import Radio from '../radio/radio';

import Button from '../button/button';
import { Copy } from '../icons';

import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormLoader } from '../loaders';
import DynamicInput from '../form-control/form-builder/form-builder-element/text';
import DynamicPhone from '../form-control/form-builder/form-builder-element/phone';
import { CountryData } from 'react-phone-input-2';
import DynamicCheckBox from '../form-control/form-builder/form-builder-element/checkbox';
import { enqueueSnackbar } from 'notistack';
import DynamicSearchSelect from '../form-control/form-builder/form-builder-element/dynamic-select';
import { useTranslation } from 'react-i18next';
import { getApiConfigSafe } from '../../utils/api-config';

interface IFormInput {
	addresses: {
		address_type?: string;
		contact_name?: string;
		default_shipping_address?: boolean;
		default_billing_address?: boolean;
		country_code?: string;
		mobile?: string;
		street_1?: string;
		street_2?: string;
		street_3?: string;
		street_name?: string;
		building_no?: string;
		additional_no?: string;
		national_address?: string;
		zip_code?: string;
		city?: string;
		district_name?: string;
		state?: number | null;
		country?: number | null;
		summary?: string;
	};
}

interface AddressModalProps {
	isOpen: boolean;
	setIsOpen: () => void;
	modalProps?: DialogProps;
	itemData?: any;
	itemIndex: number;
	items?: any;
	prefillData?: {
		contact_name?: string;
		mobile?: string;
	};
	onSave: (data: any, itemIndex?: number) => void;
	translationKey: string;
	optionalFields?: string[];
}

const addressTypeOptions = [
	{ label: 'Delivery', value: 'Delivery' },
	{ label: 'Shipping', value: 'Shipping' },
	{ label: 'Office', value: 'Office' }
];

// excludeEmptyString matters: without it yup runs the pattern against '' and fails, so clearing an
// optional field would report a format error instead of simply being empty. Note it only works in
// the options-object form - passing it as a third argument is ignored.
const zipCodeRule = Yup.string()
	.matches(/^[0-9\u0660-\u0669]+$/, {
		message: 'Zip code must contain only digits without special characters',
		excludeEmptyString: true
	})
	.test(
		'no-dot-plus',
		'Zip code must not contain "." or "+"',
		(value) => !value?.includes('.') && !value?.includes('+')
	);

const cityRule = Yup.string().matches(/^[A-Za-z\u0600-\u06FF\s]+$/, {
	message: 'City must contain alphabets only',
	excludeEmptyString: true
});

// Fields that mean a pane was actually filled in. summary, country_code and the default flags are
// derived or auto-populated, so they are not proof that the user entered anything.
const ADDRESS_CONTENT_FIELDS = [
	'address_type',
	'contact_name',
	'mobile',
	'street_1',
	'street_2',
	'street_3',
	'street_name',
	'building_no',
	'additional_no',
	'national_address',
	'zip_code',
	'city',
	'district_name',
	'state',
	'country'
];

// Every field has to be named when resetting a pane. react-hook-form leaves a field untouched if
// its name is absent from the reset payload, so an address opened for edit would keep showing its
// values on the other language's pane.
const BLANK_ADDRESS = {
	address_type: '',
	contact_name: '',
	country_code: '',
	mobile: '',
	street_1: '',
	street_2: '',
	street_3: '',
	street_name: '',
	building_no: '',
	additional_no: '',
	national_address: '',
	zip_code: '',
	city: '',
	district_name: '',
	state: null,
	country: null,
	summary: '',
	default_shipping_address: false,
	default_billing_address: false
};

const hasValues = (values?: any) =>
	!!values &&
	ADDRESS_CONTENT_FIELDS.some((field) => {
		const value = values[field];
		return value !== undefined && value !== null && String(value).trim() !== '';
	});

const buildValidationSchema = (isKSA: boolean, optionalFields: string[] = []) => {
	const zipOptional = optionalFields.includes('zip_code');
	const stateOptional = optionalFields.includes('state');

	return Yup.object().shape({
		addresses: Yup.object().shape({
			address_type: Yup.string().required('Address type is required'),
			contact_name: Yup.string().required('Contact name is required'),
			street_1: Yup.string().required('Address 1 is required'),
			street_2: Yup.string().nullable(),
			street_3: Yup.string().nullable(),
			...(isKSA ? { building_no: Yup.string().nullable(), additional_no: Yup.string().nullable() } : {}),
			zip_code: zipOptional
				? Yup.string()
						.nullable()
						.test(
							'optional-digits',
							'Zip code must contain only digits without special characters',
							(value) =>
								!value ||
								(!value.includes('.') &&
									!value.includes('+') &&
									/^[0-9]+$/.test(value))
						)
				: Yup.string()
						.required('Zip code is required')
						.matches(/^[0-9]+$/, 'Zip code must contain only digits without special characters')
						.test(
							'no-dot-plus',
							'Zip code must not contain "." or "+"',
							(value) => !value?.includes('.') && !value?.includes('+')
						),
			city: Yup.string()
				.matches(/^[A-Za-z\s]+$/, 'City must contain alphabets only'),
			state: stateOptional
				? Yup.number()
						.nullable()
						.typeError('State is not valid')
				: Yup.number()
						.typeError('State is required')
						.required('State is required'),
			country: Yup.number()
				.typeError('Country is required')
				.required('Country is required')
		})
	});
};

// The Arabic pane is a translation of an address that was already filled in English: the dropdowns
// are hidden and inherited from the English pane, so nothing here is required. Format rules that
// accept Arabic characters are kept.
const arabicValidationSchema = Yup.object().shape({
	addresses: Yup.object().shape({
		zip_code: zipCodeRule,
		city: cityRule
	})
});

const AddressModal: React.FC<AddressModalProps> = ({
	isOpen,
	setIsOpen,
	modalProps,
	itemData,
	itemIndex,
	items,
	prefillData,
	onSave,
	translationKey,
	optionalFields = [],
}) => {
	const { t } = useTranslation();
	const isKSA = getApiConfigSafe()?.countryEnv === 'ksa';
	const [submitLoading, setSubmitLoading] = useState<boolean>(false);
	const [language, setLanguage] = useState<'en' | 'ar'>('en');
	// Field values of the language that is not on screen, so switching back and forth keeps both.
	const [drafts, setDrafts] = useState<{ en?: any; ar?: any }>({});
	const [entryDetails, setEntryDetails] = useState<Record<string, any>>({
		country_data: null,
		state_data: null
	});

	const validationSchema = useMemo(
		() => buildValidationSchema(isKSA, optionalFields),
		[isKSA, optionalFields]
	);

	const { country_data, state_data } = entryDetails;

	const {
		control,
		handleSubmit,
		setValue,
		reset,
		getValues,
		watch,
		trigger,
		getFieldState
	} = useForm<IFormInput>({
		resolver: yupResolver(language === 'ar' ? arabicValidationSchema : validationSchema),
		mode: 'all'
	});

	const watchedValues = useWatch({
		control: control,
		name: [
			'addresses.contact_name',
			'addresses.street_1',
			'addresses.street_2',
			'addresses.street_3',
			'addresses.street_name',
			'addresses.building_no',
			'addresses.additional_no',
			'addresses.national_address',
			'addresses.zip_code',
			'addresses.city',
			'addresses.district_name',
			'addresses.state',
			'addresses.country'
		]
	});

	const selectedCountry = watch('addresses.country');
	const isDirtyC = getFieldState('addresses.country')?.isDirty || false;

	const onSubmit = (values: any) => {
		setSubmitLoading(true);
		handleValidateSave(values);
		setSubmitLoading(false);
	};

	// Arabic is a translation of the English address, so the English pane has to be valid before it
	// can be opened. Each pane keeps its own values while the modal stays open.
	const handleLanguageChange = async (next: 'en' | 'ar') => {
		if (next === language) {
			return;
		}

		// The gate only applies to starting the Arabic pane. Once it holds values - a new translation
		// in progress, or an existing Arabic row being edited - switching back to it is navigation,
		// not a fresh start, and must never be blocked.
		if (next === 'ar' && !hasValues(drafts.ar)) {
			const isEnglishValid = await trigger();
			if (!isEnglishValid) {
				enqueueSnackbar(
					t(`accounting.${translationKey}.msg.fill_english_address_first`, {
						defaultValue: 'Please fill the English address first'
					}),
					{ variant: 'error' }
				);
				return;
			}
		}

		const current = getValues()?.addresses;
		setDrafts((prev) => ({ ...prev, [language]: current }));
		setLanguage(next);
		reset({
			addresses: { ...BLANK_ADDRESS, ...(drafts[next] ?? {}) } as IFormInput['addresses']
		});
	};

	const handleDuplicate = async () => {
		const v = await trigger();
		if (!v) {
			return;
		}
		let values = getValues();
		values = {
			...values,
			addresses: {
				...values?.addresses,
				default_shipping_address: false,
				default_billing_address: false,
				country: country_data,
				state: state_data,
				language
			}
		};
		setSubmitLoading(true);
		onSave({ ...values });
		handleModalClose();
		setSubmitLoading(false);
	};

	const handleValidateSave = (values: any) => {
		const validateDefaults = (data: any, index: number, type = 'shipping') => {
			const u_data = [...data];
			if (index != null && index >= 0) {
				u_data?.splice(index, 1);
			}

			if (type == 'shipping') {
				const hasDFS = u_data.some((d: any) => d?.default_shipping_address);
				if (hasDFS) {
					throw new Error(
						t(`accounting.${translationKey}.msg.default_shipping_address_msg`)
					);
				}

				return false;
			} else {
				const hasDFB = u_data.some((d: any) => d?.default_billing_address);
				if (hasDFB) {
					throw new Error(
						t(`accounting.${translationKey}.msg.default_billing_address_msg`)
					);
				}
				return false;
			}
		};

		try {
			// The pane on screen holds the live values, the other one its stored draft.
			let englishValues = language === 'en' ? values?.addresses : drafts.en;
			const arabicValues = language === 'ar' ? values?.addresses : drafts.ar;

			// The default flags only exist on the English pane, so they are checked there whichever
			// pane happens to be open at save time.
			if (englishValues?.default_shipping_address) {
				validateDefaults(items, itemIndex, 'shipping');
			}

			if (englishValues?.default_billing_address) {
				validateDefaults(items, itemIndex, 'billing');
			}

			if (englishValues && items && items?.length === 0) {
				englishValues = {
					...englishValues,
					default_shipping_address: true,
					default_billing_address: true
				};
			}

			// One address is one row in the list. Its Arabic translation rides along nested under
			// `arabic` so the pair stays together, in the list and in the payload.
			if (!hasValues(englishValues) && !hasValues(arabicValues)) {
				return;
			}

			// An address that only ever had Arabic data stays Arabic-only, otherwise it would be
			// rewritten as an empty English row.
			if (!hasValues(englishValues)) {
				onSave(
					{
						...values,
						addresses: {
							...arabicValues,
							address_type: arabicValues?.address_type || itemData?.address_type,
							country: country_data,
							state: state_data,
							language: 'ar'
						}
					},
					itemIndex
				);
				handleModalClose();
				return;
			}

			const row: any = {
				...englishValues,
				country: country_data,
				state: state_data,
				language: 'en'
			};

			// Always set the key, never leave it out: the list merges the saved row over the old one, so
			// an absent `arabic` would silently keep a translation the user just cleared.
			// The dropdowns are hidden on the Arabic pane, so the backend fills those columns from the
			// English row - address_type in particular is NOT NULL on party_address_arabic.
			row.arabic = hasValues(arabicValues)
				? { ...arabicValues, id: itemData?.arabic?.id }
				: null;

			onSave({ ...values, addresses: row }, itemIndex);
			handleModalClose();
		} catch (error) {
			enqueueSnackbar(error?.message || 'Something went wrong', {
				variant: 'error'
			});
		}
	};

	const handleModalClose = () => {
		reset({ addresses: null });
		setDrafts({});
		setLanguage('en');
		setEntryDetails({
			state_data: null,
			country_data: null
		});
		setIsOpen();
	};

	const handleSetCountryCodeField = (country: CountryData) => {
		const values = getValues();
		const oldVal = values?.addresses?.country_code;
		const newVal = `+${country.dialCode}`;
		if (oldVal !== newVal) {
			setValue(`addresses.country_code` as never, newVal as never);
		}
	};

	useEffect(() => {
		const summaryVal = watchedValues.reduce((ele, ele2, index) => {
			if (!ele2) return ele;
			if (!ele) return ele2;

			if (index === 11) {
				ele2 = state_data
			}
			if (index === 12) {
				ele2 = country_data
			}

			ele = ele?.name || ele;
			ele2 = ele2?.name || ele2;
			if ([2, 3, 4, 5, 6, 7].includes(index)) {
				return ele + ' ' + ele2;
			}
			return ele + ', \n' + ele2;
		}, '');

		setValue('addresses.summary' as never, summaryVal as never);
	}, [setValue, watchedValues, country_data, state_data]);

	useEffect(() => {
		if (itemData) {
			// Keep the row on the language it was saved with, otherwise an existing Arabic address
			// would be re-tagged as English and moved to the English table on save.
			setLanguage(itemData?.language === 'ar' ? 'ar' : 'en');
			// The saved translation seeds the Arabic pane, so switching to it shows the Arabic data
			// rather than an empty form. It is held as a draft, never merged into the English pane.
			const { arabic, ...englishData } = itemData;
			setDrafts(
				arabic
					? {
						ar: {
							...BLANK_ADDRESS,
							...arabic,
							country: arabic?.country?.id ?? arabic?.country,
							state: arabic?.state?.id ?? arabic?.state
						}
					}
					: {}
			);
			reset({
				addresses: {
					...BLANK_ADDRESS,
					...englishData,
					country: englishData?.country?.id,
					state: englishData?.state?.id
				}
			});
		}
	}, [itemData, reset]);

	useEffect(() => {
		if (isOpen && !itemData) {
			setLanguage('en');
			setDrafts({});
			reset({
				addresses: {
					...BLANK_ADDRESS,
					address_type: 'Office',
					contact_name: prefillData?.contact_name || '',
					mobile: prefillData?.mobile || ''
				}
			});
		}
	}, [isOpen, itemData, prefillData?.contact_name, prefillData?.mobile, reset]);

	const setDetailsData = (data: any[], key: string) => {
		setEntryDetails((prev) => ({ ...prev, [key]: data }));
	};

	useEffect(() => {
		if (isDirtyC) {
			setValue('addresses.state', null);
		}
	}, [selectedCountry, isDirtyC]);

	const stateFilter = useMemo(() => {
		return { '&country_id.eq': selectedCountry || 0 }
	}, [selectedCountry]);

	return (
		<Modal
			open={isOpen}
			onClose={setIsOpen}
			maxWidth='md'
			fullWidth
			{...modalProps}>
			<form onSubmit={handleSubmit(onSubmit)}>
				{submitLoading && <FormLoader />}
				<DialogTitle className='addressModal--Title'>
					<Typography type='s3' color='inherit' weight='medium'>
						{t(`accounting.${translationKey}.addressModal.title`)}
					</Typography>
					<IconButton onClick={handleModalClose}>
						<Close fontSize='small' />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<RadioGroup
						className='addressModal--LanguageSwitch'
						row
						value={language}
						onChange={(e) => handleLanguageChange(e.target.value as 'en' | 'ar')}>
						<FormControlLabel value='en' control={<Radio />} label='English' />
						<FormControlLabel value='ar' control={<Radio />} label='Arabic' />
					</RadioGroup>
					<Grid container rowGap={1.5} columnSpacing={3} pt={3}>
						{language === 'en' && (
							<Grid item sm={6}>
								<DynamicSearchSelect
									label={t(`accounting.${translationKey}.addressModal.address_type_label`)}
									options={addressTypeOptions}
									name='address_type'
									placeholder={t(`accounting.${translationKey}.addressModal.address_type_placeholder`)}
									fieldArrayName='addresses'
									formControl={control}
									required
								/>
							</Grid>
						)}
						<Grid item sm={6}>
							<DynamicInput
								type='text'
								formControl={control}
								label={t(`accounting.${translationKey}.addressModal.contact_name_label`)}
								name='contact_name'
								fieldArrayName='addresses'
								placeholder={t(`accounting.${translationKey}.addressModal.contact_name_placeholder`)}
								required={language === 'en'}
							/>
						</Grid>
						<Grid item sm={6}>
							<DynamicPhone
								type='text'
								formControl={control}
								label={t(`accounting.${translationKey}.addressModal.mobile_label`)}
								name='mobile'
								fieldArrayName='addresses'
								setCountryCodeField={(country: CountryData) =>
									handleSetCountryCodeField(country)
								}
							/>
						</Grid>
						<Grid item sm={6}>
							<DynamicInput
								type='text'
								formControl={control}
								label={t(`accounting.${translationKey}.addressModal.address1_label`)}
								name='street_1'
								fieldArrayName='addresses'
								placeholder={t(`accounting.${translationKey}.addressModal.address1_placeholder`)}
								required={language === 'en'}
							/>
						</Grid>
						<Grid item sm={6}>
							<DynamicInput
								type='text'
								formControl={control}
								label={t(`accounting.${translationKey}.addressModal.address2_label`)}
								name='street_2'
								fieldArrayName='addresses'
								placeholder={t(`accounting.${translationKey}.addressModal.address2_placeholder`)}
							/>
						</Grid>
						<Grid item sm={6}>
							<DynamicInput
								type='text'
								formControl={control}
								label={t(`accounting.${translationKey}.addressModal.address3_label`)}
								name='street_3'
								fieldArrayName='addresses'
								placeholder={t(`accounting.${translationKey}.addressModal.address3_placeholder`)}
							/>
						</Grid>
						{isKSA && <Grid item sm={6}>
							<DynamicInput
								type='text'
								formControl={control}
								label={t(`accounting.${translationKey}.addressModal.streetName_label`)}
								name='street_name'
								fieldArrayName='addresses'
								placeholder={t(`accounting.${translationKey}.addressModal.streetName_placeholder`)}
							/>
						</Grid>}
						{isKSA && <>
							<Grid item sm={6}>
								<DynamicInput
									type='text'
									formControl={control}
									label={t(`accounting.${translationKey}.addressModal.buildingNo_label`)}
									name='building_no'
									fieldArrayName='addresses'
									placeholder='Enter Building No.'
								/>
							</Grid>
							<Grid item sm={6}>
								<DynamicInput
									type='text'
									formControl={control}
									label={t(`accounting.${translationKey}.addressModal.additionalNo_label`)}
									name='additional_no'
									fieldArrayName='addresses'
									placeholder='Enter Additional No.'
								/>
							</Grid>
						</>}
						{isKSA && <Grid item sm={6}>
							<DynamicInput
								type='text'
								formControl={control}
								label={t(`accounting.${translationKey}.addressModal.nationalAddress_label`)}
								name='national_address'
								fieldArrayName='addresses'
								placeholder={t(`accounting.${translationKey}.addressModal.nationalAddress_placeholder`)}
							/>
						</Grid>}
						<Grid item sm={6}>
							<DynamicInput
								type='text'
								formControl={control}
								label={t(`accounting.${translationKey}.addressModal.zip_code_label`)}
								name='zip_code'
								fieldArrayName='addresses'
								placeholder={t(`accounting.${translationKey}.addressModal.zip_code_placeholder`)}
								required={!optionalFields.includes('zip_code') && language === 'en'}
							/>
						</Grid>
						{language === 'en' && (
							<Grid item sm={6}>
								<DynamicSearchSelect
									label={t(`accounting.${translationKey}.addressModal.country_label`)}
									name='country'
									placeholder={t(`accounting.${translationKey}.addressModal.country_placeholder`)}
									fieldArrayName='addresses'
									formControl={control}
									apiType='countries'
									getSelectedData={(row: any[]) =>
										setDetailsData(row, 'country_data')
									}
									required
								/>
							</Grid>
						)}
						{language === 'en' && (
							<Grid item sm={6}>
								<DynamicSearchSelect
									label={t(`accounting.${translationKey}.addressModal.state_label`)}
									name='state'
									placeholder={t(`accounting.${translationKey}.addressModal.state_placeholder`)}
									fieldArrayName='addresses'
									formControl={control}
									apiType='states'
									customeFilter={stateFilter}
									getSelectedData={(row: any[]) =>
										setDetailsData(row, 'state_data')
									}
									required
								/>
							</Grid>
						)}
						<Grid item sm={6}>
							<DynamicInput
								label={t(`accounting.${translationKey}.addressModal.city_label`)}
								name='city'
								placeholder={t(`accounting.${translationKey}.addressModal.city_placeholder`)}
								fieldArrayName='addresses'
								formControl={control}
							/>
						</Grid>
						{isKSA && <Grid item sm={6}>
							<DynamicInput
								type='text'
								formControl={control}
								label={t(`accounting.${translationKey}.addressModal.districtName_label`)}
								name='district_name'
								fieldArrayName='addresses'
								placeholder={t(`accounting.${translationKey}.addressModal.districtName_placeholder`)}
							/>
						</Grid>}
						<Grid item sm={6}>
							<DynamicInput
								type='text'
								formControl={control}
								label={t(`accounting.${translationKey}.addressModal.summary_label`)}
								name='summary'
								fieldArrayName='addresses'
								placeholder={t(`accounting.${translationKey}.addressModal.summary_placeholder`)}
								is_multiline
								disabled
							/>
						</Grid>
						{language === 'en' && (
							<Grid item sm={12}>
								<DynamicCheckBox
									formControl={control}
									name='default_shipping_address'
									title={t(`accounting.${translationKey}.addressModal.default_shipping_address_label`)}
									fieldArrayName='addresses'
									titlePosition='end'
								/>
							</Grid>
						)}
						{language === 'en' && (
							<Grid item sm={12}>
								<DynamicCheckBox
									formControl={control}
									name='default_billing_address'
									title={t(`accounting.${translationKey}.addressModal.default_billing_address_label`)}
									fieldArrayName='addresses'
									titlePosition='end'
								/>
							</Grid>
						)}
					</Grid>
				</DialogContent>
				<DialogActions
					className={`addressModal--ActionContainer ${!itemData && 'addressModal--ActionContainer--FlexEnd'}`}>
					{itemData && (
						<Button
							className='addressModal--ActionContainer--WhiteBtn'
							startIcon={<Copy />}
							disabled={submitLoading}
							onClick={() => handleDuplicate()}>
							{t("common.duplicate")}
						</Button>
					)}
					<Box display='flex' gap={1.5}>
						<Button
							className='addressModal--ActionContainer--WhiteBtn'
							onClick={handleModalClose}
							disabled={submitLoading}>
							{t("common.cancel")}
						</Button>
						<Button
							type='submit'
							endIcon={
								submitLoading ? (
									<CircularProgress size={20} color='inherit' />
								) : undefined
							}
							disabled={submitLoading}>
							{t("common.save")}
						</Button>
					</Box>
				</DialogActions>
			</form>
		</Modal>
	);
};

export default AddressModal;
